import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Flex,
  Text,
  Image,
  IconButton,
  HStack,
  VStack,
  Box,
  useToast,
} from "@chakra-ui/react";
import { FaTimes, FaCopy } from "react-icons/fa";
import NextImage from "next/image";
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useClipboard } from "use-clipboard-copy";

import { Large } from "../../components/Type";
import { HOSTNAME } from "../../utils/request";
import WhatsappIcon from "../../assets/whatsapp_icon.svg";
import FacebookIcon from "../../assets/facebook_icon.svg";
import TelegramIcon from "../../assets/telegram_icon.svg";
import XIcon from "../../assets/x_icon.svg";

// Product public URL: <hostname>/<username>/toko/<slug>
const buildProductUrl = (username, slug) =>
  username && slug ? `${HOSTNAME}/${username}/toko/${slug}` : "";

// `url` / `message` override the store product link (e.g. a Mini Games page).
const ProductShareModal = ({ isOpen, onClose, product, username, url, message }) => {
  const { _ } = useLingui();
  const toast = useToast();
  const clipboard = useClipboard();
  const publicUrl = url || buildProductUrl(username, product?.slug);

  const shareMessage = message || _(t`Cek ${product?.name || "produk"} di toko ${username} di Saweria`);

  const handleShare = (platform) => {
    const msg = encodeURIComponent(shareMessage);
    const url = encodeURIComponent(publicUrl || "");
    const full = encodeURIComponent(`${shareMessage} ${publicUrl}`);
    let target = "";
    switch (platform) {
      case "whatsapp":
        target = `https://api.whatsapp.com/send?text=${full}`;
        break;
      case "facebook":
        target = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${msg}`;
        break;
      case "telegram":
        target = `https://t.me/share/url?url=${url}&text=${msg}`;
        break;
      case "x":
        target = `https://twitter.com/intent/tweet?text=${msg}&url=${url}`;
        break;
    }
    if (target) window.open(target, "_blank");
  };

  const handleCopy = () => {
    clipboard.copy(publicUrl);
    toast({
      title: _(t`Link berhasil disalin!`),
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="6px" maxW="500px">
        <ModalHeader fontSize="18px" fontWeight="bold" px={6} pt={4}>
          <Flex align="center" justify="space-between">
            <Large fontSize="xl" fontWeight="normal">
              <Trans>Bagikan Produk Ini</Trans>
            </Large>
            <IconButton icon={<FaTimes />} size="sm" variant="ghost" onClick={onClose} aria-label="Close" />
          </Flex>
        </ModalHeader>

        <ModalBody px={6} pb={6}>
          <VStack spacing={5} align="stretch">
            <Flex borderRadius="6px" bg="white" border="0.7px solid #a8aaaa" p={3} align="center" gap={3}>
              {product?.cover && (
                <Image src={product.cover} alt={product?.name} w="63px" h="63px" borderRadius="8px" objectFit="cover" />
              )}
              <Text flex={1} ml={6} lineHeight="20px" fontFamily="IBM Plex Mono">
                {product?.name}
              </Text>
            </Flex>

            <VStack spacing={2} align="stretch">
              <Text lineHeight="20px" fontWeight="500" fontFamily="IBM Plex Mono">
                <Trans>Bagikan ke:</Trans>
              </Text>
              <HStack spacing={2}>
                <Box cursor="pointer" onClick={() => handleShare("whatsapp")}>
                  <NextImage src={WhatsappIcon} width={40} height={40} alt="WhatsApp" />
                </Box>
                <Box cursor="pointer" onClick={() => handleShare("facebook")}>
                  <NextImage src={FacebookIcon} width={40} height={40} alt="Facebook" />
                </Box>
                <Box cursor="pointer" onClick={() => handleShare("telegram")}>
                  <NextImage src={TelegramIcon} width={40} height={40} alt="Telegram" />
                </Box>
                <Box cursor="pointer" onClick={() => handleShare("x")}>
                  <NextImage src={XIcon} width={40} height={40} alt="X" />
                </Box>
              </HStack>
            </VStack>

            <Flex borderRadius="8px" bg="#eaefed" p={3} align="center" gap={4}>
              <Text flex={1} lineHeight="20px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" fontFamily="IBM Plex Mono">
                {publicUrl}
              </Text>
              <IconButton icon={<FaCopy />} size="sm" variant="ghost" onClick={handleCopy} aria-label="Copy" />
            </Flex>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export { ProductShareModal };
